import { useState, useMemo, useEffect, useCallback, type ReactNode } from 'react';
import { formatCurrency, MatchStatus, BetStatus, UserTier } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { useBetting } from '@/hooks/useBetting';
import { useUserManagement, type FightSetting } from '@/hooks/useUserManagement';
import { useLanguage } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronRight, ChevronDown } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Wallet Balance Formula Reference (from Screenshot4845 spec)
//
// Agent / Player leaf row columns:
//   D = Original Bet Placed  (= E for player)
//   E = Downline: Bet Placed  [BLACK]
//   F = Downline: Rebate     = E*(1-parentTP)  [BLACK]
//   G = Downline: Win/Lose   (wl for players; = child's wlC*-1 for agents)  [BLACK]
//   H = Downline: Total      = (-1*E)+F+G  [GREEN/RED per spec]
//   J = Fight: Bet Placed    = E*fightPct  [BLACK]
//   K = Fight: Rebate        = (-J*parentRebate)+(E*parentRebate)-F  [GREEN/RED]
//   L = Fight: Win/Lose      = G*fightPct*-1  [GREEN/RED]
//   M = Fight: Total         = J+K+L  [GREEN/RED]
//   N = Company: Bet Placed  = D-J  [BLACK]
//   O = Company: Rebate      = -N*(1-parentTP)  [GREEN/RED]
//   P = Company: Win/Lose    = -(G+L)  [GREEN/RED]
//   Q = Company: Total       = N+O+P  [GREEN/RED]
//
// Company view agent header rows = companyAgentRow(agent.ownSummary) per spec
// Agent view detail rows = agentAsChildRow(child.ownSummary) — salmon colored
// ─────────────────────────────────────────────────────────────────────────────

const fmtTs = (ts: string): string => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return '-'; }
};

type FightSettingMap = Record<string, Record<string, { fightTicket: number; fightPct: number }>>;

interface BetRow {
  id: string;
  userId: string;
  matchId: string;
  selection: string;
  amount: number;
  oddsAtTime: number;
  status: BetStatus;
  createdAt: string;
  ticketPrice: number;       // child's own ticket price (as ×100 integer)
  fightPercentage: number;   // child's own fight% (integer e.g. 10 for 10%)
  fightPctSnapshot?: number;
  /** Compounded fight% across all ancestor levels at bet time (stored by settle_fight_chain) */
  fightPctSnapshotChain?: number;
  userTier: UserTier;
  isOwnBet: boolean;
}

/**
 * 11 settlement columns matching Book3:
 *   D = origBP    (original bet placed)
 *   E = bpT       (downline bet placed)
 *   F = commT     (downline commission)
 *   G = wlT       (downline win/lose)
 *   H = totalT    (downline total)
 *   I = fightPct  (displayed separately, not a number column)
 *   J = bpF       (fight bet placed)
 *   K = wlF       (fight win/lose)
 *   L = totalF    (fight total)
 *   M = bpC       (company bet placed)
 *   N = commC     (company commission)
 *   O = wlC       (company win/lose)
 *   P = totalC    (company total)
 */
interface BookRow {
  origBP: number;                                           // D
  bpT: number; commT: number; wlT: number; totalT: number; // E F G H
  bpF: number; commF: number; wlF: number; totalF: number; // J K L M (Book5: 4 Fight cols)
  bpC: number; commC: number; wlC: number; totalC: number; // N O P Q
  // Optional display overrides (don't affect formula chain, only what's rendered)
  commCDisplay?: number;   // overrides commC cell in Company group
  bpFDisplay?: number;     // overrides bpF cell in Fight group
  commFDisplay?: number;   // overrides commF cell in Fight group
  wlFDisplay?: number;     // overrides wlF cell in Fight group
  totalFDisplay?: number;  // overrides totalF cell in Fight group
}

const emptyBookRow = (): BookRow => ({
  origBP: 0,
  bpT: 0, commT: 0, wlT: 0, totalT: 0,
  bpF: 0, commF: 0, wlF: 0, totalF: 0,
  bpC: 0, commC: 0, wlC: 0, totalC: 0,
});

/**
 * LEAF ROW — player (or agent) detail row in agent report (spec Screenshot4845)
 *
 * bp       = raw bet amount
 * wl       = actual win/lose (positive if player wins, 0 if player lost)
 * childTP  = player's own ticket price as decimal (e.g. 0.96 for 4% rebate)
 * parentTP = parent agent's ticket price as decimal (e.g. 0.80)
 * fightPct = fight% as decimal (e.g. 0.1 for 10%)
 *
 * D = E = bp
 * F = E*(1-childTP)                          [Downline Rebate — player's own rebate on bp]
 * G = wl                                     [Downline Win — BLACK]
 * H = (-1*E)+F+G                             [Downline Total — GREEN/RED per spec]
 * J = E*fightPct                             [Fight Bet Placed — BLACK]
 * K = (-J*parentRebate)+(E*parentRebate)-F  [Fight Rebate — GREEN/RED per spec]
 * L = G*fightPct*-1                         [Fight Win — GREEN/RED]
 * M = J+K+L                                 [Fight Total — GREEN/RED]
 * N = D-J                                   [Company Bet Placed — BLACK]
 * O = -N*(1-parentTP)                       [Company Rebate — GREEN/RED]
 * P = -(G+L)                                [Company Win — GREEN/RED]
 * Q = N+O+P                                 [Company Total — GREEN/RED]
 */
function leafRow(
  bp: number, wl: number,
  childTP: number, parentTP: number, fightPct: number
): BookRow {
  const origBP = bp;
  const bpT    = bp;
  const commT  = bpT * (1 - childTP);           // F = E*(1-childTP)  [Downline Rebate — player's own rebate]
  const wlT    = wl;                             // G = wl (raw win/lose)  [BLACK]
  const totalT = (-bpT) + commT + wlT;          // H = (-1*E)+F+G  [Downline Total per spec]
  const bpF    = bpT * fightPct;                // J = E*fightPct  [Fight Bet Placed — BLACK]
  // K = (-1*J * parentRebate) + (E * parentRebate) - F  [Fight Rebate per spec]
  const parentRebate = 1 - parentTP;
  const commF  = (-bpF * parentRebate) + (bpT * parentRebate) - commT;
  const wlF    = wlT * fightPct * -1;           // L = G*fightPct*-1  [Fight Win/Lose — GREEN/RED]
  const totalF = bpF + commF + wlF;             // M = J+K+L  [Fight Total — GREEN/RED]
  const bpC    = origBP - bpF;                  // N = D-J  [Company Bet Placed — BLACK]
  const commC  = -bpC * (1 - parentTP);         // O = -N*(1-parentTP)  [Company Rebate — GREEN/RED]
  const wlC    = -(wlT + wlF);                  // P = -(G+L)  [Company Win/Lose — GREEN/RED]
  const totalC = bpC + commC + wlC;             // Q = N+O+P  [Company Total — GREEN/RED]
  return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
}

/**
 * AGENT OWN-VIEW HEADER ROW — grand total of the agent's own settlement (Book6)
 * = Sum of all child display rows (Header Player Rows + Agent Downline Rows)
 *
 * All columns: direct sum of children's corresponding columns.
 * totalT = sum of children's totalT (each child's H column)
 * totalF = bpF + commF + wlF
 * totalC = bpC + commC + wlC
 */
function agentSumRow(childrenSum: BookRow): BookRow {
  const origBP = childrenSum.origBP;
  const bpT    = childrenSum.bpT;
  const commT  = childrenSum.commT;
  const wlT    = childrenSum.wlT;
  const totalT = childrenSum.totalT;   // sum of children's totalT (each = raw win)
  const bpF    = childrenSum.bpF;
  const commF  = childrenSum.commF;
  const wlF    = childrenSum.wlF;
  const totalF = bpF + commF + wlF;
  const bpC    = childrenSum.bpC;
  const commC  = childrenSum.commC;
  const wlC    = childrenSum.wlC;
  const totalC = bpC + commC + wlC;
  return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
}

/**
 * AGENT AS CHILD ROW — how the parent (agent) sees a child agent (salmon/detail row).
 *
 * Spec (Section 2 - agent upline view, agent detail rows):
 *   D  = child's origBP
 *   E  = child's bpC  (child's Company Bet Placed → parent's Downline Bet Placed)
 *   F  = E * (1 - childTP)                    [Downline Rebate — BLACK]
 *   G  = child's wlC * -1                     [Downline Win/Lose — BLACK]
 *   H  = (-1*E) + F + G                       [Downline Total — GREEN/RED per spec]
 *   J  = E * fightPct  (0 if fight OFF/0% at timestamp)  [Fight Bet Placed — BLACK]
 *   K  = (-1*J * parentRebate) + (E * parentRebate) - F  [Fight Rebate]
 *      In spec notation: =(-1*Fight BP * viewer rebate)+(DL BP * viewer rebate)-(DL Rebate)
 *   L  = -1 * G * fightPct  (0 if fight OFF/0% at timestamp)  [Fight Win/Lose — GREEN/RED]
 *        fightPct is timestamped (derived from fightPctSnapshotChain), 0 when fight was OFF
 *   M  = J + K + L                            [Fight Total — GREEN/RED]
 *   N  = E - J                                [Company Bet Placed — BLACK]
 *   O  = -N * (1 - parentTP)                  [Company Rebate — GREEN/RED]
 *   P  = -(G + L)                             [Company Win/Lose — GREEN/RED]
 *   Q  = N + O + P                            [Company Total — GREEN/RED]
 */
function agentAsChildRow(
  childOwnSummary: BookRow,
  childTP: number,   // child's ticket price as decimal (e.g. 0.80 for Agent1.1)
  parentTP: number,  // parent's ticket price as decimal (e.g. 0.70 for Agent1)
  fightPct: number   // parent's fight% against this child as decimal
): BookRow {
  const origBP = childOwnSummary.origBP;        // D = child's origBP
  const bpT    = childOwnSummary.bpC;           // E = child's bpC
  const commT  = bpT * (1 - childTP);           // F = E*(1-childTP)  [Downline Rebate — child agent's rebate rate]
  const wlT    = childOwnSummary.wlC * -1;      // G = child's wlC * -1  [Downline Win/Lose]
  const totalT = (-bpT) + commT + wlT;          // H = (-1*E)+F+G  per spec
  const bpF    = fightPct > 0 ? bpT * fightPct : 0;  // J = E*fightPct (0 if fight OFF at timestamp)
  // K = (-1*J * parentRebate) + (E * parentRebate) - F  [Fight Rebate per spec]
  const parentRebate = 1 - parentTP;
  const commF  = (-bpF * parentRebate) + (bpT * parentRebate) - commT;
  const wlF    = fightPct > 0 ? (-1 * wlT * fightPct) : 0;  // L = -1*G*fightPct (0 if fight OFF)
  const totalF = bpF + commF + wlF;             // M = J+K+L  [Fight Total]
  const bpC    = bpT - bpF;                     // N = E-J  [Company Bet Placed]
  const commC  = -bpC * (1 - parentTP);         // O = -N*(1-parentTP)  [Company Rebate]
  const wlC    = -(wlT + wlF);                  // P = -(G+L)  [Company Win/Lose]
  const totalC = bpC + commC + wlC;             // Q = N+O+P  [Company Total]
  return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
}

/**
 * COMPANY VIEW AGENT ROW — how the company sees a direct child agent.
 *
 * Spec (Section 1 - company view):
 *   Original Bet: agent's origBP
 *   Downline BP:      = agent's Company Bet Placed (= agent.bpC)
 *   Downline Rebate:  = agent's Company Rebate * -1  (= -agent.commC, positive number)
 *   Downline W/L:     = agent's Company Win/Lose * -1 (= -agent.wlC, positive number)
 *   Downline Total:   = DL Rebate + DL Win/Lose  (NOT -E+F+G, just sum of the two)
 *   Fight group:      ALL BLANK (zero, not shown for company level)
 *   Company BP:       = Downline BP  (= agent.bpC)
 *   Company Rebate:   = Downline Rebate * -1  (= agent.commC, negative)
 *   Company W/L:      = Downline W/L * -1  (= agent.wlC, negative)
 *   Company Total:    = CM BP + CM Rebate + CM W/L
 */
function companyAgentRow(agentOwnSummary: BookRow): BookRow {
  const origBP = agentOwnSummary.origBP;
  // Downline group
  const bpT    = agentOwnSummary.bpC;            // DL BP = agent's Company Bet Placed
  const commT  = -agentOwnSummary.commC;          // DL Rebate = agent's Company Rebate * -1
  const wlT    = -agentOwnSummary.wlC;            // DL W/L = agent's Company Win/Lose * -1
  const totalT = (-bpT) + commT + wlT;              // DL Total = (-1*DL BP) + DL Rebate + DL W/L
  // Fight group: ALL BLANK for company level
  const bpF = 0, commF = 0, wlF = 0, totalF = 0;
  // Company group
  const bpC    = bpT;                             // CM BP = DL BP
  const commC  = -commT;                          // CM Rebate = DL Rebate * -1 = agent's commC
  const wlC    = -wlT;                            // CM W/L = DL W/L * -1 = agent's wlC
  const totalC = bpC + commC + wlC;              // CM Total = CM BP + CM Rebate + CM W/L
  return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
}

// ── [Book6] Company view agent row uses companyAgentRow() function above.
// No transformation needed — just pass through ownSummary.

const addRows = (a: BookRow, b: BookRow): BookRow => ({
  origBP: a.origBP + b.origBP,
  bpT:   a.bpT   + b.bpT,   commT:  a.commT  + b.commT,
  wlT:   a.wlT   + b.wlT,   totalT: a.totalT + b.totalT,
  bpF:   a.bpF   + b.bpF,   commF:  a.commF  + b.commF,
  wlF:   a.wlF   + b.wlF,   totalF: a.totalF + b.totalF,
  bpC:   a.bpC   + b.bpC,   commC:  a.commC  + b.commC,
  wlC:   a.wlC   + b.wlC,   totalC: a.totalC + b.totalC,
});

function getDescendantIds(rootId: string, allUsers: { id: string; parentId?: string }[]): Set<string> {
  const ids = new Set<string>();
  const visit = (id: string) => {
    ids.add(id);
    allUsers.filter(u => u.parentId === id).forEach(child => visit(child.id));
  };
  visit(rootId);
  return ids;
}

const col = (v: number) => v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : '';

// ── Book3 color palette ────────────────────────────────────────────────────
// Downline group: Excel theme3 blue (#4472C4)
const CLR_DL_HDR = '#4472C4';   // header bg
const CLR_DL_TXT = '#FFFFFF';   // header text
// Fight group: Excel gold (#FFC000)
const CLR_FT_HDR = '#FFC000';
const CLR_FT_TXT = '#000000';
const CLR_FT_COL = '#7F6000';   // darker for readability on white bg
// Company group: Excel theme8 teal-green (#70AD47 / #375623)
const CLR_CM_HDR = '#70AD47';
const CLR_CM_TXT = '#FFFFFF';
const CLR_CM_COL = '#375623';

// Border styles for each group
const BL_TOT = { borderLeft:  `2px solid ${CLR_DL_HDR}` };
const BR_TOT = { borderRight: `2px solid ${CLR_DL_HDR}` };
const BL_FGT = { borderLeft:  `2px solid ${CLR_FT_COL}` };
const BR_FGT = { borderRight: `2px solid ${CLR_FT_COL}` };
const BL_COM = { borderLeft:  `2px solid ${CLR_CM_COL}` };
const BR_COM = { borderRight: `2px solid ${CLR_CM_COL}` };

function C({ v, style, bold, noColor }: { v: number; style?: React.CSSProperties; bold?: boolean; noColor?: boolean }) {
  return (
    <TableCell
      className={`text-right font-mono whitespace-nowrap text-xs ${noColor ? '' : col(v)} ${bold ? 'font-bold' : ''}`}
      style={noColor ? { color: '#000000', ...style } : style}
    >
      {formatCurrency(v)}
    </TableCell>
  );
}

// ── Player flat view ──────────────────────────────────────────────────────────
function PlayerFlatTable({ bets, allMatches, t }: { bets: BetRow[]; allMatches: any[]; t: any }) {
  const rows = bets.map(bet => {
    const isWin = bet.status === BetStatus.WON;
    const wl = isWin ? bet.amount * bet.oddsAtTime : 0;
    const D = bet.amount;
    const F = D * (1 - bet.ticketPrice / 100);  // commission
    const G = wl;
    const H = (G + F) - D;
    return { bet, D, F, G, H };
  });
  const totD = rows.reduce((s, r) => s + r.D, 0);
  const totF = rows.reduce((s, r) => s + r.F, 0);
  const totG = rows.reduce((s, r) => s + r.G, 0);
  const totH = rows.reduce((s, r) => s + r.H, 0);
  return (
    <div className="rounded-md border overflow-x-auto text-[6pt] lg:text-base bg-white">
      <Table>
        <TableHeader className="bg-gray-100">
          <TableRow>
            <TableHead className="font-semibold">{t.wallet.dateTime}</TableHead>
            <TableHead className="font-semibold">{t.wallet.matchCol}</TableHead>
            <TableHead className="text-center font-semibold">{t.wallet.score}</TableHead>
            <TableHead className="text-right font-semibold">{t.wallet.odd}</TableHead>
            <TableHead className="text-right font-semibold">{t.wallet.betPlaced}</TableHead>
            <TableHead className="text-right font-semibold">{t.wallet.commission}</TableHead>
            <TableHead className="text-right font-semibold">{t.wallet.winLose}</TableHead>
            <TableHead className="text-right font-semibold">{t.wallet.total}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">{t.wallet.noBets}</TableCell></TableRow>
          ) : (
            <>
              {rows.map(({ bet, D, F, G, H }) => {
                const match = allMatches.find((m: any) => m.id === bet.matchId);
                return (
                  <TableRow key={bet.id} className="bg-white hover:bg-gray-50">
                    <TableCell className="font-mono text-xs">{fmtTs(bet.createdAt)}</TableCell>
                    <TableCell className="text-xs">{match ? `${match.homeTeam} vs ${match.awayTeam}` : 'Unknown'}</TableCell>
                    <TableCell className="text-center font-mono font-semibold">{bet.selection === 'Any Other Score' ? 'AOS' : (bet.selection || '-')}</TableCell>
                    <TableCell className="text-right font-mono">{(bet.oddsAtTime || 0).toFixed(2)}</TableCell>
                    <C v={D} noColor /><C v={F} noColor /><C v={G} noColor /><C v={H} bold />
                  </TableRow>
                );
              })}
              <TableRow className="bg-gray-100 font-bold border-t-2">
                <TableCell colSpan={4}>{t.wallet.grandTotal}</TableCell>
                <C v={totD} noColor /><C v={totF} noColor /><C v={totG} noColor /><C v={totH} bold />
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Recursive settlement computation ─────────────────────────────────────────
type UserMeta = {
  id: string; username: string; nickname?: string;
  parentId?: string; tier: UserTier;
  // ticketPrice stored ×100 (e.g. 90 for 0.9); formulas always call /100
  ticketPrice: number;
  fightPercentage: number; // current value, used as fallback (integer, e.g. 10 for 10%)
  /** fight% mode of THIS user as a parent: 'single' | 'multiple' */
  fightPctMode?: 'single' | 'multiple';
  /** fight% the parent charges THIS user (used when parent is in 'multiple' mode) */
  parentFightPct?: number;
  /** When false, fight features are disabled and fightPct treated as 0 for all calculations */
  fightOnOff?: boolean;
};

/**
 * A snapshot group: bets that share the same fightPctSnapshot value.
 */
interface PlayerSnapshotGroup {
  fightPctInt: number;    // integer snapshot value (e.g. 10 for 10%)
  row: BookRow;           // computed leafRow sum for this group
  bets: BetRow[];         // original bets in this group (for detail sub-rows)
}

/**
 * Get bet's effective fight% as decimal.
 */
/**
 * For a player, group their bets by fightPctSnapshot.
 */
function groupPlayerBetsBySnapshot(
  bets: BetRow[],
  fallbackFightPct: number,
  playerTP: number,   // decimal e.g. 0.95
  parentTP: number,   // decimal e.g. 0.9
): PlayerSnapshotGroup[] {
  const groupMap = new Map<number, BetRow[]>();
  for (const bet of bets) {
    // Use the stored snapshot fight% if available (set at bet placement time).
    // For bets placed before snapshot was introduced (null), fall back to 0
    // so they appear in their own group rather than being merged with current rate.
    const snap = (bet.fightPctSnapshot !== undefined && bet.fightPctSnapshot !== null)
      ? bet.fightPctSnapshot
      : 0;  // old bets with no snapshot → grouped as 0%
    const list = groupMap.get(snap) || [];
    list.push(bet);
    groupMap.set(snap, list);
  }
  const groups: PlayerSnapshotGroup[] = [];
  for (const [fightPctInt, groupBets] of groupMap.entries()) {
    const H = fightPctInt / 100;
    let combined = emptyBookRow();
    for (const bet of groupBets) {
      if (bet.status === BetStatus.CANCELLED) continue; // voided — zero contribution
      const isWin = bet.status === BetStatus.WON;
      const wl = isWin ? bet.amount * bet.oddsAtTime : 0;
      combined = addRows(combined, leafRow(bet.amount, wl, playerTP, parentTP, H));
    }
    groups.push({ fightPctInt, row: combined, bets: groupBets });
  }
  groups.sort((a, b) => a.fightPctInt - b.fightPctInt);
  return groups;
}

/**
 * Recursively compute settlement rows for a node.
 *
 * Returns:
 *   ownSummary     — agent's own grand-total (D,E..P columns as seen by this agent)
 *   parentViewRow  — how the PARENT sees this node as a single combined row
 *   snapshotGroups — for PLAYER nodes: per-fightPct groups
 */
/**
 * Derive the historical fight% a parent had against a direct child agent at the time a bet was placed.
 * Uses fightPctSnapshotChain (full ancestry chain) and fightPctSnapshot (direct child→player leg).
 * Falls back to liveParentFightPct if chain data is unavailable.
 */
function deriveParentFightPctForBet(bet: BetRow, liveParentFightPct: number): number {
  if (bet.fightPctSnapshotChain != null && bet.fightPctSnapshot != null) {
    const chainRatio    = 1 - bet.fightPctSnapshotChain / 100;
    const snapshotRatio = 1 - bet.fightPctSnapshot      / 100;
    if (snapshotRatio > 0.0001) {
      const derived = (1 - chainRatio / snapshotRatio) * 100;
      return Math.round(derived * 100) / 100;
    }
    return bet.fightPctSnapshotChain;
  }
  return liveParentFightPct;
}

/**
 * Collect all player bets that are descendants of nodeId (through any depth).
 * Returns flat array sorted by createdAt.
 */
function collectPlayerBetsUnder(
  nodeId: string,
  allUsers: UserMeta[],
  betsByUser: Map<string, BetRow[]>,
): BetRow[] {
  const result: BetRow[] = [];
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const u = allUsers.find(x => x.id === current);
    if (u?.tier === UserTier.PLAYER) {
      result.push(...(betsByUser.get(current) ?? []));
    }
    allUsers.filter(x => x.parentId === current).forEach(c => queue.push(c.id));
  }
  return result.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function computeNode(
  nodeId: string,
  allUsers: UserMeta[],
  betsByUser: Map<string, BetRow[]>,
  parentTP: number,  // parent's TP ×100
  fightPct: number,  // integer, parent's LIVE fight% vs this node (used as fallback only)
): { ownSummary: BookRow; parentViewRow: BookRow; snapshotGroups: PlayerSnapshotGroup[] } | null {
  const user = allUsers.find(u => u.id === nodeId);
  if (!user) return null;

  if (user.tier === UserTier.PLAYER) {
    const bets = betsByUser.get(nodeId) || [];
    if (bets.length === 0) return null;
    const playerTP  = user.ticketPrice / 100;
    const pTP       = parentTP / 100;
    // groupPlayerBetsBySnapshot uses bet.fightPctSnapshot per bet — already snapshot-correct
    const snapshotGroups = groupPlayerBetsBySnapshot(bets, fightPct, playerTP, pTP);
    const combined = snapshotGroups.reduce((acc, g) => addRows(acc, g.row), emptyBookRow());
    return { ownSummary: combined, parentViewRow: combined, snapshotGroups };
  }

  // Agent node: recurse into children
  const children = allUsers.filter(u => u.parentId === nodeId);
  const childDisplayRows: BookRow[] = [];

  for (const child of children) {
    // For player children: snapshot fight% is handled inside groupPlayerBetsBySnapshot.
    // Pass live fight% as the initial value; groupPlayerBetsBySnapshot will override per-bet.
    const rawEffectivePct = child.parentFightPct ?? 0;
    const effectiveChildFightPct = (user.fightOnOff === false) ? 0 : rawEffectivePct;
    const result = computeNode(child.id, allUsers, betsByUser, user.ticketPrice, effectiveChildFightPct);
    if (!result) continue;

    if (child.tier === UserTier.PLAYER) {
      // Player child: parentViewRow already uses per-bet fightPctSnapshot (via groupPlayerBetsBySnapshot)
      childDisplayRows.push(result.parentViewRow);
    } else {
      // Agent child: must use per-bet historical fight% derived from fightPctSnapshotChain.
      // Group descendant player bets by the historical fight% this parent had against this agent.
      // Each group computes its own agentAsChildRow so the numbers reflect what was true at bet time.
      const descBets = collectPlayerBetsUnder(child.id, allUsers, betsByUser);

      if (descBets.length === 0) continue;

      // Build snapshot-derived fight% bands: consecutive bets with the same derived fight%
      const snapshotBands: Array<{ pct: number; bets: BetRow[] }> = [];
      for (const bet of descBets) {
        const pct = deriveParentFightPctForBet(bet, effectiveChildFightPct);
        const last = snapshotBands[snapshotBands.length - 1];
        if (last && Math.abs(last.pct - pct) < 0.005) {
          last.bets.push(bet);
        } else {
          snapshotBands.push({ pct, bets: [bet] });
        }
      }

      // For each band, restrict betsByUser to band bets and re-run computeNode
      // using the band's snapshot-derived fight%. Then sum all bands into one combined row.
      let combinedAgentRow = emptyBookRow();
      for (const band of snapshotBands) {
        const bandPct = Math.round(band.pct);
        const bandBetIds = new Set(band.bets.map(b => b.id));
        const restrictedMap = new Map<string, BetRow[]>();
        for (const [uid, ubets] of betsByUser.entries()) {
          const filtered = ubets.filter(b => bandBetIds.has(b.id));
          if (filtered.length > 0) restrictedMap.set(uid, filtered);
        }
        const bandResult = computeNode(child.id, allUsers, restrictedMap, user.ticketPrice, bandPct);
        if (!bandResult) continue;
        const bandAsChild = agentAsChildRow(
          bandResult.ownSummary,
          child.ticketPrice / 100,
          user.ticketPrice / 100,
          bandPct / 100
        );
        combinedAgentRow = addRows(combinedAgentRow, bandAsChild);
      }
      childDisplayRows.push(combinedAgentRow);
    }
  }

  if (childDisplayRows.length === 0) return null;

  const childrenSum = childDisplayRows.reduce(addRows, emptyBookRow());
  const ownSummary = agentSumRow(childrenSum);

  // parentViewRow: how THIS agent looks to its parent.
  // For the parent's perspective, also use per-bet derived fight% rather than live fightPct.
  // Group THIS node's descendant player bets by the grandparent's historical fight% against this node.
  const ownDescBets = collectPlayerBetsUnder(nodeId, allUsers, betsByUser);
  let parentViewRow: BookRow;
  if (ownDescBets.length > 0) {
    // Build bands for grandparent→this relationship
    const pvBands: Array<{ pct: number; bets: BetRow[] }> = [];
    for (const bet of ownDescBets) {
      const pct = fightPct; // grandparent's fight% against this node — use passed-in value (may be 0 or live)
      // NOTE: for the parentViewRow we can't derive grandparent's fight from chain/snapshot
      // without knowing the grandparent's ID. Use live fightPct as the best estimate.
      const last = pvBands[pvBands.length - 1];
      if (last && Math.abs(last.pct - pct) < 0.005) {
        last.bets.push(bet);
      } else {
        pvBands.push({ pct, bets: [bet] });
      }
    }
    // Since we use a single fightPct value for all bets, just use agentAsChildRow on ownSummary
    parentViewRow = agentAsChildRow(
      ownSummary,
      user.ticketPrice / 100,
      parentTP / 100,
      fightPct / 100
    );
  } else {
    parentViewRow = agentAsChildRow(
      ownSummary,
      user.ticketPrice / 100,
      parentTP / 100,
      fightPct / 100
    );
  }

  return { ownSummary, parentViewRow, snapshotGroups: [] };
}

// ── Salmon colour constants ────────────────────────────────────────────────────
// Salmon = warm pinkish-orange tint for agent sub-rows
const SALMON_BG     = '#FFF0EC';
const SALMON_BORDER = '#F9BDB0';
const SALMON_TEXT   = '#7F1D1D';

/**
 * AgentFightBand: one timestamp band for an agent node — collects all bets
 * placed while the viewer's fight% against this agent was `fightPctInt`.
 * Used to render separate salmon rows per fight% period.
 */
interface AgentFightBand {
  fightPctInt: number;   // viewer's fight% at bet time (integer, e.g. 4 for 4%)
  fromTs: string;        // ISO timestamp of first bet in this band
  row: BookRow;          // agentAsChildRow computed with this fight%
}

/**
 * Given all player bets under an agent subtree, group them by the viewer's
 * historical fight% against the agent (derived from fightPctSnapshotChain and
 * fightPctSnapshot), then compute agentAsChildRow per group.
 *
 * @param agentOwnSummary  The agent's own grand total (ownSummary from computeNode).
 * @param allDescBets      All player bets under the agent, sorted chronologically.
 * @param childTP          Agent's ticket price as decimal.
 * @param parentTP         Viewer's ticket price as decimal.
 * @param liveAgentFightPct Current live fight% (fallback when chain data missing).
 */
/**
 * Build fight% timestamp bands for an agent node.
 * Groups all descendant player bets by the viewer's historical fight% against the agent
 * (derived from fightPctSnapshotChain and fightPctSnapshot).
 * For each band, computes agentAsChildRow using the band's specific fight%.
 *
 * @param nodeId           The agent's user ID.
 * @param allDescBets      All player bets under this agent, sorted chronologically.
 * @param childTP          Agent's ticket price as decimal (e.g. 0.80).
 * @param parentTP         Viewer's ticket price as decimal (e.g. 0.70).
 * @param liveAgentFightPct Current live fight% as integer (fallback when chain unavailable).
 * @param allUsers         All UserMeta (for re-running computeNode per band).
 * @param betsByUser       Full bet map (restricted per band inside).
 */
function buildAgentFightBands(
  nodeId: string,
  allDescBets: BetRow[],
  _childTP: number,
  parentTP: number,
  liveAgentFightPct: number,
  allUsers: UserMeta[],
  betsByUser: Map<string, BetRow[]>,
): AgentFightBand[] {
  if (allDescBets.length === 0) return [];

  // Derive viewer's fight% against this agent at each bet's time.
  // Formula: viewerPct = 1 - (1 - chain/100) / (1 - snapshot/100)
  const getPctForBet = (bet: BetRow): number => {
    if (bet.fightPctSnapshotChain != null && bet.fightPctSnapshot != null) {
      const chainRatio    = 1 - bet.fightPctSnapshotChain / 100;
      const snapshotRatio = 1 - bet.fightPctSnapshot      / 100;
      if (snapshotRatio > 0.0001) {
        const derived = (1 - chainRatio / snapshotRatio) * 100;
        return Math.round(derived * 100) / 100;
      }
      return bet.fightPctSnapshotChain;
    }
    // No chain data — fall back to live setting
    return liveAgentFightPct;
  };

  // Group consecutive bets with the same derived fight% into bands
  const rawBands: Array<{ pct: number; bets: BetRow[]; fromTs: string }> = [];
  for (const bet of allDescBets) {
    const pct = getPctForBet(bet);
    const last = rawBands[rawBands.length - 1];
    if (last && Math.abs(last.pct - pct) < 0.005) {
      last.bets.push(bet);
    } else {
      rawBands.push({ pct, bets: [bet], fromTs: bet.createdAt });
    }
  }

  // Only create multiple bands if there are actually different fight% values
  if (rawBands.length <= 1) return [];

  return rawBands.map(band => {
    const bandBetIds = new Set(band.bets.map(b => b.id));
    // Restrict betsByUser to only this band's bets
    const restrictedMap = new Map<string, BetRow[]>();
    for (const [uid, ubets] of betsByUser.entries()) {
      const filtered = ubets.filter(b => bandBetIds.has(b.id));
      if (filtered.length > 0) restrictedMap.set(uid, filtered);
    }
    const bandFightPct = Math.round(band.pct);
    // Re-run computeNode for this agent with only band bets and band fight%
    const result = computeNode(nodeId, allUsers, restrictedMap, parentTP * 100, bandFightPct);
    return {
      fightPctInt: bandFightPct,
      fromTs: band.fromTs,
      row: result?.parentViewRow ?? emptyBookRow(),
    };
  });
}

// ── Tree node renderer ────────────────────────────────────────────────────────
interface TreeNodeProps {
  nodeId: string;
  /** For agent children: agentAsChildRow (parent's perspective = salmon row value = header value).
   *  For player children: leafRow combined.
   *  Header row always equals this displayRow (= sum of salmon rows for agents). */
  displayRow: BookRow;
  ownSummary: BookRow;       // agent's own grand-total (for expanding children)
  fightPctInt: number;       // integer fight% parent charges this node
  /** ISO timestamp of when the parent last updated fight% against this node */
  fightUpdatedAt?: string;
  snapshotGroups: PlayerSnapshotGroup[];
  allUsers: UserMeta[];
  betsByUser: Map<string, BetRow[]>;
  showDetails: boolean;
  allMatches: any[];
  depth: number;
  parentTP: number;          // parent's TP ×100
  isCompanyView: boolean;
  /** Callback when user clicks "View as [agent]" link */
  onViewAs?: (agentId: string) => void;
  /** Callback when user clicks "View [player]'s Report" link */
  onViewAsPlayer?: (playerId: string) => void;
}

/** Render the data cells for one BookRow (12 data columns + optional fight% cell) */
function BookRowCells({
  row, showDetails, bold, indent, label, fightPctLabel,
  allMatches, bet, expandIcon
}: {
  row: BookRow; showDetails: boolean; bold?: boolean;
  indent: number; label?: ReactNode; fightPctLabel?: string;
  allMatches?: any[]; bet?: BetRow; expandIcon?: ReactNode;
}) {
  const match = bet && allMatches ? allMatches.find((m: any) => m.id === bet.matchId) : null;
  return (
    <>
      <TableCell>
        <div className="flex items-center gap-1" style={{ paddingLeft: `${indent}px` }}>
          {expandIcon}
          {label ? (
            <span className="text-xs font-semibold">{label}</span>
          ) : bet ? (
            <span className="text-muted-foreground text-[10px]">
              {showDetails ? bet.selection : (
                <>
                  {`${fmtTs(bet.createdAt)}${match ? ` — ${match.homeTeam} vs ${match.awayTeam}` : ''} [${bet.selection}]`}
                  {(bet.fightPctSnapshot != null && bet.fightPctSnapshot > 0) && (
                    <span className="ml-1 font-semibold" style={{ color: '#92400e' }}>
                      吃成 {bet.fightPctSnapshot}%
                    </span>
                  )}
                </>
              )}
            </span>
          ) : null}
        </div>
      </TableCell>
      {showDetails && (
        bet ? (
          <>
            <TableCell className="font-mono text-[10px] text-muted-foreground">{fmtTs(bet.createdAt)}</TableCell>
            <TableCell className="text-[10px] text-muted-foreground">{match ? `${match.homeTeam} vs ${match.awayTeam}` : '—'}</TableCell>
            <TableCell className="text-right font-mono text-[10px]" style={BL_TOT}>{(bet.oddsAtTime || 0).toFixed(2)}</TableCell>
          </>
        ) : (
          <><TableCell /><TableCell /><TableCell style={BL_TOT} /></>
        )
      )}
      {/* Downline group: origBP(black), bpT(black), commT(black), wlT(black), totalT(green/red) */}
      <C v={Math.max(0, row.origBP)}  style={BL_TOT} bold={bold} noColor />
      <C v={Math.max(0, row.bpT)}     bold={bold} noColor />
      <C v={row.commT}   bold={bold} noColor />
      <C v={row.wlT}     bold={bold} noColor />
      <C v={row.totalT}  bold={bold} style={BR_TOT} />
      {/* Fight group: bpF(black) — bet-placed amount, always ≥ 0 */}
      <C v={Math.max(0, row.bpFDisplay !== undefined ? row.bpFDisplay : row.bpF)} style={BL_FGT} bold={bold} noColor />
      <C v={row.commFDisplay !== undefined ? row.commFDisplay : row.commF} bold={bold} />
      <C v={row.wlFDisplay   !== undefined ? row.wlFDisplay   : row.wlF}   bold={bold} />
      <C v={row.totalFDisplay !== undefined ? row.totalFDisplay : row.totalF} bold={bold} style={BR_FGT} />
      {/* Company group: bpC(black) — bet-placed amount, always ≥ 0 */}
      <C v={Math.max(0, row.bpC)}    style={BL_COM} bold={bold} noColor />
      <C v={row.commCDisplay !== undefined ? row.commCDisplay : row.commC} bold={bold} />
      <C v={row.wlC}    bold={bold} />
      <C v={row.totalC} bold={bold} style={BR_COM} />
    </>
  );
}

/** Renders a single snapshot group for a player with mixed fight% */
function SnapshotGroupRows({
  group, displayName, indent, showDetails, userTP, parentTP, allMatches
}: {
  group: PlayerSnapshotGroup; displayName: string; indent: number;
  showDetails: boolean; userTP: number; parentTP: number;
  allMatches: any[];
}) {
  const [grpExpanded, setGrpExpanded] = useState(false);
  const hasMultiBets = group.bets.length > 1;
  // fightPctInt=0 means the bet had no snapshot recorded (placed before snapshot feature)
  const pctLabel = group.fightPctInt > 0 ? `${group.fightPctInt}%` : '—';
  const groupLabel = group.fightPctInt > 0
    ? `${displayName} (${group.fightPctInt}%)`
    : `${displayName}`;
  return (
    <>
      <TableRow
        className={`${hasMultiBets ? 'cursor-pointer' : ''} bg-yellow-50 hover:bg-yellow-100 font-semibold`}
        onClick={() => hasMultiBets && setGrpExpanded(e => !e)}
      >
        <BookRowCells
          row={group.row} showDetails={showDetails}
          bold={true} indent={indent}
          label={
            <span className="flex items-center gap-1">
              {hasMultiBets && (grpExpanded
                ? <ChevronDown className="h-3 w-3 shrink-0" />
                : <ChevronRight className="h-3 w-3 shrink-0" />)}
              <span style={{ color: CLR_FT_COL }} className="font-semibold">{groupLabel}</span>
            </span>
          }
          fightPctLabel={pctLabel}
          allMatches={allMatches}
        />
      </TableRow>
      {grpExpanded && hasMultiBets && group.bets.filter(bet => bet.status !== BetStatus.CANCELLED).map(bet => {
        const H = group.fightPctInt / 100;
        const isWin = bet.status === BetStatus.WON;
        const wl = isWin ? bet.amount * bet.oddsAtTime : 0;
        const br = leafRow(bet.amount, wl, userTP / 100, parentTP / 100, H);
        return (
          <TableRow key={bet.id} className="bg-white hover:bg-gray-50">
            <BookRowCells
              row={br} showDetails={showDetails}
              indent={indent + 14} bet={bet} allMatches={allMatches}
              fightPctLabel={pctLabel}
            />
          </TableRow>
        );
      })}
    </>
  );
}

// Helper: collect ALL descendant player bets for an agent node
function collectDescendantBets(
  nodeId: string,
  allUsers: UserMeta[],
  betsByUser: Map<string, BetRow[]>,
): BetRow[] {
  const result: BetRow[] = [];
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentUser = allUsers.find(u => u.id === current);
    if (currentUser?.tier === UserTier.PLAYER) {
      const bets = betsByUser.get(current) ?? [];
      result.push(...bets);
    }
    const children = allUsers.filter(u => u.parentId === current);
    for (const child of children) queue.push(child.id);
  }
  return result.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function TreeNode({
  nodeId, displayRow, ownSummary, fightPctInt, fightUpdatedAt, snapshotGroups, allUsers, betsByUser,
  showDetails, allMatches, depth, parentTP, isCompanyView, onViewAs, onViewAsPlayer
}: TreeNodeProps) {
  // useState MUST come before any conditional return (Rules of Hooks)
  const [isExpanded, setIsExpanded] = useState(false);
  const user = allUsers.find(u => u.id === nodeId);
  if (!user) return null;

  const isPlayer = user.tier === UserTier.PLAYER;
  const displayName = user.nickname || user.username;
  const indent = depth * 14;
  const children = allUsers.filter(u => u.parentId === nodeId);

  const hasMixedSnapshots = isPlayer && snapshotGroups.length > 1;
  const hasSingleSnapshot = isPlayer && snapshotGroups.length === 1;
  const hasBetSubRows = hasSingleSnapshot && (snapshotGroups[0]?.bets.length ?? 0) > 1;

  // ── Fight% label logic ────────────────────────────────────────────────────
  // Determine snapshot consistency for players
  const allSnapshotsMatch = isPlayer && snapshotGroups.length > 0 &&
    snapshotGroups.every(g => g.fightPctInt === fightPctInt);
  const hasDiffFromCurrent = isPlayer && snapshotGroups.length > 0 && !allSnapshotsMatch;

  // Fight% label for the (hidden) Fight% column cell
  let summaryFightPctLabel = '—';
  if (isPlayer) {
    if (hasDiffFromCurrent) {
      summaryFightPctLabel = '(diff %)';
    } else if (fightPctInt > 0) {
      summaryFightPctLabel = `${fightPctInt}%`;
    } else if (snapshotGroups.length === 1) {
      const pct = snapshotGroups[0].fightPctInt;
      summaryFightPctLabel = pct > 0 ? `${pct}%` : '—';
    } else {
      summaryFightPctLabel = '—';
    }
  } else {
    summaryFightPctLabel = fightPctInt > 0 ? `${fightPctInt}%` : '—';
  }

  // Fight% suffix appended to the display name in the label column
  let fightPctSuffix = '';
  if (isPlayer) {
    if (hasDiffFromCurrent) {
      fightPctSuffix = fightPctInt > 0 ? ` (${fightPctInt}% / diff %)` : ' (diff %)';
    } else if (fightPctInt > 0) {
      fightPctSuffix = ` (${fightPctInt}%)`;
    } else if (snapshotGroups.length === 1 && snapshotGroups[0].fightPctInt > 0) {
      fightPctSuffix = ` (${snapshotGroups[0].fightPctInt}%)`;
    }
  } else {
    if (fightPctInt > 0) fightPctSuffix = ` (${fightPctInt}%)`;
  }

  // Row background based on depth and role
  const summaryBgClass = isCompanyView
    ? (isPlayer ? 'bg-blue-50 hover:bg-blue-100' : 'bg-blue-100 hover:bg-blue-200')
    : depth === 0 ? 'bg-blue-50 hover:bg-blue-100'
    : depth === 1 ? (isPlayer ? 'bg-gray-50 hover:bg-gray-100' : 'bg-indigo-50 hover:bg-indigo-100')
    : 'bg-white hover:bg-gray-50';

  // Agent node can always expand to show its salmon row (+ "View as" link)
  // Player node can expand if it has multiple bets, mixed snapshots, or onViewAsPlayer is set
  const canExpand = !isPlayer || hasBetSubRows || hasMixedSnapshots || hasDiffFromCurrent || !!onViewAsPlayer;

  // In company view, non-player agent rows use the username as a direct link to drill in
  const isCompanyAgentRow = isCompanyView && !isPlayer && onViewAs;

  return (
    <>
      {/* ── Summary / header row ── */}
      <TableRow
        className={`${(canExpand && !isCompanyAgentRow) ? 'cursor-pointer' : ''} font-semibold ${summaryBgClass}`}
        onClick={() => (canExpand && !isCompanyAgentRow) && setIsExpanded(e => !e)}
      >
        <BookRowCells
          row={displayRow}
          showDetails={showDetails}
          bold={true}
          indent={indent}
          label={
            <span className="flex items-center gap-1">
              {isCompanyAgentRow ? (
                /* Company view: username is a clickable link — no chevron */
                <button
                  className="font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onViewAs && onViewAs(nodeId); }}
                >
                  {displayName}
                </button>
              ) : (
                <>
                  {canExpand && (isExpanded
                    ? <ChevronDown className="h-3 w-3 shrink-0" />
                    : <ChevronRight className="h-3 w-3 shrink-0" />)}
                  <span className="flex flex-col gap-0">
                    <span>{displayName}</span>
                    {/* fight% sub-label removed per user request */}
                  </span>
                </>
              )}
            </span>
          }
          fightPctLabel={summaryFightPctLabel}
          allMatches={allMatches}
        />
      </TableRow>

      {isExpanded && (
        <>
          {/* ── AGENT: salmon row(s) – one per fight% timestamp band ── */}
          {!isPlayer && (() => {
            // Collect all descendant player bets and build fight% bands
            const descBets = collectDescendantBets(nodeId, allUsers, betsByUser);
            const bands = buildAgentFightBands(
              nodeId,
              descBets,
              user.ticketPrice / 100,
              parentTP / 100,
              fightPctInt,
              allUsers,
              betsByUser,
            );

            // If multiple bands (different fight% periods), render one salmon row per band
            // Otherwise (single or no band), render the regular single salmon row
            if (bands.length > 1) {
              return bands.map((band, idx) => (
                <TableRow
                  key={`band-${idx}`}
                  style={{ backgroundColor: SALMON_BG }}
                  className="hover:brightness-95"
                >
                  <BookRowCells
                    row={band.row}
                    showDetails={showDetails}
                    bold={false}
                    indent={indent + 14}
                    label={
                      <span className="flex items-center gap-1">
                        <span style={{ color: SALMON_TEXT }} className="flex flex-col gap-0">
                          <span className="text-[10px] font-semibold">
                            {displayName}{band.fightPctInt > 0 ? ` 吃成 ${band.fightPctInt}%` : ''}
                          </span>
                          <span className="text-[8px] font-normal opacity-70">
                            from {fmtTs(band.fromTs)}
                          </span>
                        </span>
                      </span>
                    }
                    fightPctLabel={band.fightPctInt > 0 ? `${band.fightPctInt}%` : '—'}
                    allMatches={allMatches}
                  />
                </TableRow>
              ));
            }

            // Single fight% — render the original salmon row
            return (
              <TableRow style={{ backgroundColor: SALMON_BG }}
                className="hover:brightness-95">
                <BookRowCells
                  row={displayRow}
                  showDetails={showDetails}
                  bold={false}
                  indent={indent + 14}
                  label={
                    <span className="flex items-center gap-1">
                      <span style={{ color: SALMON_TEXT }} className="flex flex-col gap-0">
                        <span className="text-[10px] font-semibold">
                          {displayName}{fightPctInt > 0 ? ` 吃成 ${fightPctInt}%` : ''}
                        </span>
                        {fightPctInt > 0 && fightUpdatedAt && (
                          <span className="text-[8px] font-normal opacity-70">{fmtTs(fightUpdatedAt)}</span>
                        )}
                      </span>
                    </span>
                  }
                  fightPctLabel={fightPctInt > 0 ? `${fightPctInt}%` : '—'}
                  allMatches={allMatches}
                />
              </TableRow>
            );
          })()}

          {/* ── AGENT: "View as [agent]" link row ── */}
          {!isPlayer && onViewAs && (
            <TableRow style={{ backgroundColor: SALMON_BG, borderBottom: `1px solid ${SALMON_BORDER}` }}>
              <TableCell colSpan={100} className="py-1.5">
                <div style={{ paddingLeft: `${indent + 20}px` }}>
                  <button
                    onClick={e => { e.stopPropagation(); onViewAs(nodeId); }}
                    className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline underline-offset-2 transition-colors"
                  >
                    🔍 View {displayName}&apos;s Report →
                  </button>
                </div>
              </TableCell>
            </TableRow>
          )}

          {/* ── PLAYER: Mixed snapshots OR bets at diff rate from current → show per-group rows ── */}
          {(hasMixedSnapshots || hasDiffFromCurrent) && snapshotGroups.map((group) => (
            <SnapshotGroupRows
              key={group.fightPctInt}
              group={group}
              displayName={displayName}
              indent={indent + 14}
              showDetails={showDetails}
              userTP={user.ticketPrice}
              parentTP={parentTP}
              allMatches={allMatches}
            />
          ))}

          {/* ── PLAYER: Single snapshot same as current, multiple bets → show individual bet sub-rows ── */}
          {hasBetSubRows && !hasDiffFromCurrent && snapshotGroups[0].bets.filter(bet => bet.status !== BetStatus.CANCELLED).map(bet => {
            const H = snapshotGroups[0].fightPctInt / 100;
            const isWin = bet.status === BetStatus.WON;
            const wl = isWin ? bet.amount * bet.oddsAtTime : 0;
            const br = leafRow(bet.amount, wl, user.ticketPrice / 100, parentTP / 100, H);
            return (
              <TableRow key={bet.id} className="bg-white hover:bg-gray-50">
                <BookRowCells
                  row={br} showDetails={showDetails}
                  indent={indent + 20} bet={bet} allMatches={allMatches}
                  fightPctLabel={snapshotGroups[0].fightPctInt > 0 ? `${snapshotGroups[0].fightPctInt}%` : '—'}
                />
              </TableRow>
            );
          })}

          {/* ── PLAYER: "View [player]'s Report" link at bottom of expanded rows ── */}
          {isPlayer && onViewAsPlayer && (
            <TableRow className="bg-yellow-50 border-b border-yellow-200">
              <TableCell colSpan={100} className="py-1.5">
                <div style={{ paddingLeft: `${indent + 20}px` }}>
                  <button
                    onClick={e => { e.stopPropagation(); onViewAsPlayer(nodeId); }}
                    className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline underline-offset-2 transition-colors"
                  >
                    🔍 View {displayName}&apos;s Report →
                  </button>
                </div>
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </>
  );
}

// ── WalletPanel (main export) ─────────────────────────────────────────────────
export function WalletPanel() {
  const { user } = useAuth();
  const { matches, bets } = useBetting();
  const { users, getFightSetting } = useUserManagement();
  const { t } = useLanguage();

  // Tournament week schedule (MYT dates)
  const ALL_WEEKS = [
    { from: '2026-06-08', to: '2026-06-14' },
    { from: '2026-06-15', to: '2026-06-21' },
    { from: '2026-06-22', to: '2026-06-28' },
    { from: '2026-06-29', to: '2026-07-05' },
    { from: '2026-07-06', to: '2026-07-12' },
    { from: '2026-07-13', to: '2026-07-19' },
    { from: '2026-07-20', to: '2026-07-26' },
  ];

  // Helper: today / yesterday in MYT (UTC+8) as yyyy-MM-dd
  const todayMYT = () => {
    const myt = new Date(Date.now() + 8 * 3600_000);
    return myt.toISOString().slice(0, 10);
  };
  const yesterdayMYT = () => {
    const myt = new Date(Date.now() + 8 * 3600_000 - 86_400_000);
    return myt.toISOString().slice(0, 10);
  };

  // "Display date" respects the 3:30 PM MYT daily cutoff:
  // Before 3:30 PM MYT (= before 07:30 UTC), the current display date is still YESTERDAY.
  // From 3:30 PM MYT onward, the current display date is TODAY.
  const displayDateMYT = () => {
    const nowUtcMs = Date.now();
    const mytDate = new Date(nowUtcMs + 8 * 3600_000); // shift to MYT
    const mytHour   = mytDate.getUTCHours();
    const mytMinute = mytDate.getUTCMinutes();
    const isPast1530 = mytHour > 15 || (mytHour === 15 && mytMinute >= 30);
    if (isPast1530) {
      // current MYT calendar date
      return mytDate.toISOString().slice(0, 10);
    } else {
      // still in previous day's window — show yesterday's MYT date
      return new Date(nowUtcMs + 8 * 3600_000 - 86_400_000).toISOString().slice(0, 10);
    }
  };
  const displayYesterdayMYT = () => {
    // one day before displayDateMYT
    const base = new Date(displayDateMYT() + 'T00:00:00Z').getTime();
    return new Date(base - 86_400_000).toISOString().slice(0, 10);
  };

  // Derive "This Week" and "Last Week" dynamically based on today's MYT date
  const getThisWeek = () => {
    const today = todayMYT();
    return ALL_WEEKS.find(w => today >= w.from && today <= w.to) ?? ALL_WEEKS[ALL_WEEKS.length - 1];
  };
  const getLastWeek = () => {
    const thisIdx = ALL_WEEKS.findIndex(w => { const today = todayMYT(); return today >= w.from && today <= w.to; });
    return thisIdx > 0 ? ALL_WEEKS[thisIdx - 1] : ALL_WEEKS[0];
  };

  // Format a week range as "08/06 → 14/06"
  const fmtWeekRange = (w: { from: string; to: string }) =>
    `${w.from.slice(5).replace('-','/')} → ${w.to.slice(5).replace('-','/')}`;

  const _displayToday = displayDateMYT();
  const [datePreset, setDatePreset] = useState<string>('today');
  const [dateFrom, setDateFrom] = useState(_displayToday);
  const [dateTo, setDateTo] = useState(_displayToday);
  const [appliedFrom, setAppliedFrom] = useState(_displayToday);
  const [appliedTo, setAppliedTo] = useState(_displayToday);
  const [selectedMatchId, setSelectedMatchId] = useState('all');
  const [showDetails, setShowDetails] = useState(false);
  const [_fightSettings, setFightSettings] = useState<FightSettingMap>({});
  /** When non-null, we render the report "as if" we are this agent (drill-down view) */
  const [viewAsAgentId, setViewAsAgentId] = useState<string | null>(null);
  /** When non-null, show a simplified player flat-table report for that player */
  const [viewAsPlayerId, setViewAsPlayerId] = useState<string | null>(null);

  // Apply a preset button
  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    let from = '', to = '';
    if (preset === 'today') {
      from = to = displayDateMYT();
    } else if (preset === 'yesterday') {
      from = to = displayYesterdayMYT();
    } else if (preset === 'lastweek') {
      const w = getLastWeek(); from = w.from; to = w.to;
    } else if (preset === 'thisweek') {
      const w = getThisWeek(); from = w.from; to = w.to;
    }
    setDateFrom(from); setDateTo(to);
    setAppliedFrom(from); setAppliedTo(to);
    setSelectedMatchId('all');
  };

  const handleSearch = () => { setAppliedFrom(dateFrom); setAppliedTo(dateTo); setSelectedMatchId('all'); };
  const handleClear  = () => {
    const d = displayDateMYT();
    setDatePreset('today');
    setDateFrom(d); setDateTo(d);
    setAppliedFrom(d); setAppliedTo(d);
    setSelectedMatchId('all');
  };

  // When a specific match is selected, auto-set the date range to that match's display date.
  // Cut-off rule: display date D covers D 15:30 MYT → (D+1) 14:29:59 MYT (= D 07:30 UTC → (D+1) 06:29:59 UTC).
  // To find D for a match: subtract 7.5 h from the match UTC time and take the date part.
  const handleMatchSelect = (matchId: string) => {
    setSelectedMatchId(matchId);
    if (matchId === 'all') return;
    const match = matches.find(m => m.id === matchId);
    if (!match) return;
    // Shift match time back by 7.5 h to land in the correct display-date bucket
    const shifted = new Date(new Date(match.startTime).getTime() - 7.5 * 3600_000);
    const displayDate = shifted.toISOString().slice(0, 10);
    setDatePreset('');
    setDateFrom(displayDate);
    setDateTo(displayDate);
    setAppliedFrom(displayDate);
    setAppliedTo(displayDate);
  };

  const isPlayer = user?.tier === UserTier.PLAYER;

  const completedMatches = useMemo(() =>
    matches
      .filter(m => m.status === MatchStatus.COMPLETED)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()),
  [matches]);

  const visibleUserIds = useMemo(() => {
    if (!user) return new Set<string>();
    if (user.tier === UserTier.COMPANY || user.tier === UserTier.MASTER) {
      const all = new Set(users.map(u => u.id)); all.add(user.id); return all;
    }
    return getDescendantIds(user.id, users);
  }, [user, users]);

  // 3:30 PM MYT cut-off rule:
  // Display date D → actual window: D 15:30 MYT → (D+1) 14:29:59 MYT
  // In UTC: D 07:30:00 UTC → (D+1) 06:29:59 UTC
  //
  // from date 16/06 → from = 16/06 07:30:00 UTC  (= 16/06 15:30 MYT)
  // to   date 16/06 → to   = 17/06 06:29:59 UTC  (= 17/06 14:29:59 MYT)
  const buildCutoffWindow = (fromDate: string, toDate: string) => {
    // "from" boundary: fromDate 07:30:00 UTC  =  fromDate 15:30 MYT
    const from = fromDate
      ? new Date(fromDate + 'T07:30:00.000Z').getTime()
      : null;
    // "to" boundary: toDate+1 06:29:59 UTC  =  (toDate+1) 14:29:59 MYT
    const to = toDate
      ? (() => {
          const d = new Date(toDate + 'T06:29:59.999Z');
          d.setUTCDate(d.getUTCDate() + 1);
          return d.getTime();
        })()
      : null;
    return { from, to };
  };

  const filteredBets = useMemo(() => {
    const { from, to } = buildCutoffWindow(appliedFrom, appliedTo);
    return bets.filter(bet => {
      if (!visibleUserIds.has(bet.userId)) return false;
      const match = matches.find(m => m.id === bet.matchId);
      if (!match || match.status !== MatchStatus.COMPLETED) return false;
      if (bet.status !== BetStatus.WON && bet.status !== BetStatus.LOST) return false;
      const matchTime = new Date(match.startTime).getTime();
      if (from !== null && matchTime < from) return false;
      if (to   !== null && matchTime > to)   return false;
      if (selectedMatchId !== 'all' && bet.matchId !== selectedMatchId) return false;
      return true;
    });
  }, [bets, matches, appliedFrom, appliedTo, selectedMatchId, visibleUserIds]);

  const loadFightSettings = useCallback(async (matchIds: string[]) => {
    if (!user || matchIds.length === 0) return;
    const newSettings: FightSettingMap = {};
    await Promise.all(matchIds.map(async (matchId) => {
      try {
        const { data: json, error } = await supabase.functions.invoke('fight_settings_2026_05_06', {
          body: { action: 'get', userId: user.id, matchId },
        });
        if (!error && json?.success && json?.data) newSettings[matchId] = json.data;
      } catch (e) { console.error('loadFightSettings error', e); }
    }));
    setFightSettings(prev => ({ ...prev, ...newSettings }));
  }, [user]);

  const filteredMatchIds = useMemo(() => Array.from(new Set(filteredBets.map(b => b.matchId))), [filteredBets]);
  useEffect(() => {
    if (filteredMatchIds.length > 0) loadFightSettings(filteredMatchIds);
  }, [filteredMatchIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrichedBets = useMemo((): BetRow[] =>
    filteredBets.map(bet => {
      const bu = users.find(u => u.id === bet.userId) ?? null;
      const tier = (bu?.tier ?? (user?.id === bet.userId ? user?.tier : UserTier.PLAYER) ?? UserTier.PLAYER) as UserTier;
      return {
        ...bet,
        ticketPrice: (bu?.ticketPrice ?? bet.ticketPrice ?? 1) * 100,  // stored ×100
        fightPercentage: bu?.fightPercentage ?? bet.fightPercentage ?? 0,
        fightPctSnapshot: (bet as any).fightPctSnapshot,
        fightPctSnapshotChain: (bet as any).fightPctSnapshotChain,
        userTier: tier,
        isOwnBet: bet.userId === user?.id,
      };
    }),
    [filteredBets, users, user]
  );

  const betsByUser = useMemo(() => {
    const map = new Map<string, BetRow[]>();
    enrichedBets.forEach(bet => {
      const list = map.get(bet.userId) || [];
      list.push(bet);
      map.set(bet.userId, list);
    });
    return map;
  }, [enrichedBets]);

  const allUsersForTree = useMemo((): UserMeta[] =>
    users.map(u => {
      // Use live fight_settings data (getFightSetting) for parentFightPct and fightOnOff
      // rather than the possibly-stale parent_fight_pct column on the users table.
      // This ensures agent→agent fight% (e.g. agent99's 4% against agent999)
      // is correctly reflected in all Fight column calculations.
      const liveSetting = u.parentId ? getFightSetting(u.parentId, u.id) : null;
      const parentFightPct = liveSetting ? (liveSetting.fightOnOff ? liveSetting.fightPct : 0) : (u.parentFightPct ?? 0);
      const fightOnOff = liveSetting ? liveSetting.fightOnOff : (u.fightOnOff !== false);
      return {
        id: u.id,
        username: u.username,
        nickname: u.nickname,
        parentId: u.parentId,
        tier: u.tier as UserTier,
        ticketPrice: (u.ticketPrice ?? 1) * 100, // stored ×100
        fightPercentage: u.fightPercentage ?? 0,  // integer (e.g. 10 for 10%)
        fightPctMode: u.fightPctMode,
        parentFightPct,
        fightOnOff,
      };
    }),
    [users, getFightSetting]
  );

  // isCompanyViewer and treeRootIds MUST be before early return (Rules of Hooks)
  const isCompanyViewer = !!(user && (user.tier === UserTier.COMPANY || user.tier === UserTier.MASTER));

  // The "effective viewer" — either the real user or the agent we are viewing as
  const effectiveViewerId = viewAsAgentId ?? user?.id ?? null;
  const effectiveViewerUser = effectiveViewerId
    ? allUsersForTree.find(u => u.id === effectiveViewerId) ?? null
    : null;

  // treeRootIds: direct children of the effective viewer
  const treeRootIds = useMemo(() => {
    if (!user || !effectiveViewerId) return [];
    const usersWithBets = new Set(enrichedBets.map(b => b.userId));
    const allVisible = users.filter(u => visibleUserIds.has(u.id));
    const rootIds: string[] = [];
    if (!viewAsAgentId && (user.tier === UserTier.COMPANY || user.tier === UserTier.MASTER)) {
      // Company view: show own bets + direct children
      const directChildren = allVisible.filter(u => u.parentId === user.id);
      if (usersWithBets.has(user.id)) rootIds.push(user.id);
      directChildren.forEach(u => rootIds.push(u.id));
    } else {
      // Agent view (or "view as agent"): show only direct children of effective viewer
      const directChildren = allVisible.filter(u => u.parentId === effectiveViewerId);
      directChildren.forEach(u => rootIds.push(u.id));
    }
    return [...new Set(rootIds)];
  }, [user, users, visibleUserIds, enrichedBets, viewAsAgentId, effectiveViewerId]);

  if (!user) return null;

  const hasBets = enrichedBets.length > 0;

  // viewerTP ×100 — use effective viewer's ticket price for salmon row calculations
  const viewerTP = ((effectiveViewerUser?.ticketPrice ?? (user.ticketPrice ?? 1) * 100)) ;
  // Note: effectiveViewerUser.ticketPrice is already ×100 (stored in allUsersForTree ×100)

  // Column count (Fight % column hidden; Book5: Fight group now has 4 cols):
  // Name(1) + origBP(1) + downline(4) + fight(4) + company(4) = 14
  // With details: Name(1) + dt(1) + match(1) + odd(1) + origBP(1) + downline(4) + fight(4) + company(4) = 17
  const totalCols = showDetails ? 17 : 14;

  // ── Filter controls ──────────────────────────────────────────────────────
  const filterControls = (
    <div className="flex flex-col gap-2 mb-4 w-full">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">📅 Match Date Range</span>
        <button onClick={handleClear}
          className="ml-auto text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors">
          ↺ Reset
        </button>
      </div>
      {/* Date inputs + Search button row */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-muted-foreground">{t.wallet.dateFrom}</label>
          <input type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setDatePreset(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring" />
        </div>
        <span className="text-muted-foreground font-bold pb-1.5 shrink-0">→</span>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-muted-foreground">{t.wallet.dateTo}</label>
          <input type="date" value={dateTo} min={dateFrom || undefined}
            onChange={e => { setDateTo(e.target.value); setDatePreset(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring" />
        </div>
        <button onClick={handleSearch}
          className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shrink-0 self-end">
          🔍 Search
        </button>
        {/* Quick-select preset buttons */}
        <div className="flex items-end gap-1 self-end flex-wrap">
          {(() => {
            const isCompanyLevel = user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;
            return ([
              { id: 'today',     label: 'Today',     sub: displayDateMYT().slice(5).replace('-','/') },
              { id: 'yesterday', label: 'Yesterday', sub: displayYesterdayMYT().slice(5).replace('-','/') },
              { id: 'lastweek',  label: 'Last Week', sub: fmtWeekRange(getLastWeek()) },
              { id: 'thisweek',  label: 'This Week', sub: fmtWeekRange(getThisWeek()) },
            ] as { id: string; label: string; sub: string }[]).map(btn => {
              // Hide date sub-label on Today/Yesterday for non-company tiers
              const showSub = isCompanyLevel || (btn.id !== 'today' && btn.id !== 'yesterday');
              return (
                <button
                  key={btn.id}
                  onClick={() => applyPreset(btn.id)}
                  className={[
                    'h-9 flex flex-col items-center justify-center px-2.5 rounded-md border text-[10px] leading-tight transition-colors shrink-0',
                    datePreset === btn.id
                      ? 'bg-primary text-primary-foreground border-primary font-semibold'
                      : 'bg-background text-muted-foreground border-input hover:bg-muted hover:text-foreground',
                  ].join(' ')}
                >
                  <span className="font-semibold text-[11px]">{btn.label}</span>
                  {showSub && <span className="opacity-75">{btn.sub}</span>}
                </button>
              );
            });
          })()}
        </div>
      </div>
      <div className="flex flex-col gap-0.5">
        <label className="text-xs text-muted-foreground font-medium">{t.wallet.match}</label>
        <Select value={selectedMatchId} onValueChange={handleMatchSelect}>
          <SelectTrigger><SelectValue placeholder={t.wallet.allMatches} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.wallet.allMatches}</SelectItem>
            {completedMatches.map(m => (
              <SelectItem key={m.id} value={m.id}>
                {m.homeTeam} vs {m.awayTeam}{m.result ? ` (${m.result})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Showing label removed as per user request */}
    </div>
  );

  // ── Player view ──────────────────────────────────────────────────────────
  if (isPlayer) {
    return (
      <div>
        {filterControls}
        <PlayerFlatTable bets={enrichedBets} allMatches={matches} t={t} />
      </div>
    );
  }

  // ── "View as agent" helper ─────────────────────────────────────────────────
  const viewAsUser = viewAsAgentId ? allUsersForTree.find(u => u.id === viewAsAgentId) : null;

  // ── "View as player" — show player flat table if set ────────────────────
  if (viewAsPlayerId) {
    const playerUser = allUsersForTree.find(u => u.id === viewAsPlayerId);
    const playerName = playerUser?.nickname || playerUser?.username || 'Player';
    const playerBets = enrichedBets.filter(b => b.userId === viewAsPlayerId);
    return (
      <div>
        {filterControls}
        <div className="flex items-center gap-3 mb-3 px-4 py-2 rounded-md border border-yellow-300 bg-yellow-50">
          <span className="text-xs text-yellow-800 font-semibold">
            👤 Viewing {playerName}&apos;s Report
          </span>
          <button
            onClick={() => setViewAsPlayerId(null)}
            className="ml-auto flex items-center gap-1 text-xs px-3 py-1 rounded bg-yellow-600 text-white hover:bg-yellow-700 font-semibold transition-colors"
          >
            ← Back to My Report
          </button>
        </div>
        <PlayerFlatTable bets={playerBets} allMatches={matches} t={t} />
      </div>
    );
  }

  // ── Company / Agent view ─────────────────────────────────────────────────
  return (
    <div>
      {filterControls}

      {/* "Viewing as agent" banner */}
      {viewAsAgentId && viewAsUser && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2 rounded-md border border-blue-300 bg-blue-50">
          <span className="text-xs text-blue-700 font-semibold">
            👁 Viewing report as: <span className="font-bold text-blue-900">{viewAsUser.nickname || viewAsUser.username}</span>
          </span>
          <button
            onClick={() => setViewAsAgentId(null)}
            className="ml-auto flex items-center gap-1 text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 font-semibold transition-colors"
          >
            ← Back to My Report
          </button>
        </div>
      )}

      <div className="rounded-md border overflow-x-auto text-[6pt] lg:text-base bg-white">
        <Table>
          <TableHeader>
            {/* Row 1: group headers */}
            <TableRow>
              {/* Username — rowSpan 2 */}
              <TableHead rowSpan={2} className="font-semibold align-middle border-r border-gray-300 bg-gray-100">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowDetails(v => !v)}
                    className="inline-flex items-center justify-center w-5 h-5 rounded border border-border bg-background hover:bg-muted text-xs font-bold leading-none shrink-0"
                    title={showDetails ? 'Hide detail columns' : 'Show detail columns'}
                  >{showDetails ? '−' : '+'}</button>
                  <span>{t.wallet.username}</span>
                </div>
              </TableHead>
              {/* Detail cols — rowSpan 2 */}
              {showDetails && (
                <>
                  <TableHead rowSpan={2} className="font-semibold align-middle text-xs bg-gray-100">{t.wallet.dateTime}</TableHead>
                  <TableHead rowSpan={2} className="font-semibold align-middle text-xs bg-gray-100">{t.wallet.matchCol}</TableHead>
                  <TableHead rowSpan={2} className="text-right font-semibold align-middle text-xs bg-gray-100" style={BL_TOT}>{t.wallet.odd}</TableHead>
                </>
              )}
              {/* Original Bet Placed — rowSpan 2 */}
              <TableHead rowSpan={2} className="text-right font-semibold align-middle text-xs whitespace-nowrap" style={{ ...BL_TOT, backgroundColor: CLR_DL_HDR, color: CLR_DL_TXT }}>
                Original<br />Bet
              </TableHead>
              {/* Downline group — 4 cols */}
              <TableHead colSpan={4} className="text-center font-bold text-xs"
                style={{ backgroundColor: CLR_DL_HDR, color: CLR_DL_TXT }}>
                {t.wallet.downline}
              </TableHead>
              {/* Fight group — 4 cols (Book5: added Commission(F)) */}
              <TableHead colSpan={4} className="text-center font-bold text-xs"
                style={{ backgroundColor: CLR_FT_HDR, color: CLR_FT_TXT, ...BR_FGT }}>
                {t.wallet.fight}
              </TableHead>
              {/* Company group — 4 cols */}
              <TableHead colSpan={4} className="text-center font-bold text-xs"
                style={{ backgroundColor: CLR_CM_HDR, color: CLR_CM_TXT, ...BL_COM, ...BR_COM }}>
                {t.wallet.company}
              </TableHead>
            </TableRow>
            {/* Row 2: sub-column labels */}
            <TableRow>
              {/* Downline sub-cols */}
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_DL_HDR }}>{t.wallet.betPlacedCol}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_DL_HDR }}>{t.wallet.commission}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_DL_HDR }}>{t.wallet.winLose}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_DL_HDR, ...BR_TOT }}>{t.wallet.total}</TableHead>
              {/* Fight sub-cols (Book5: 4 columns including Commission(F)) */}
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_FT_COL, ...BL_FGT }}>{t.wallet.betPlacedCol}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_FT_COL }}>{t.wallet.commission}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_FT_COL }}>{t.wallet.winLoseCol}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_FT_COL, ...BR_FGT }}>{t.wallet.total}</TableHead>
              {/* Company sub-cols */}
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_CM_COL, ...BL_COM }}>{t.wallet.betPlacedCol}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_CM_COL }}>{t.wallet.commission}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_CM_COL }}>{t.wallet.winLoseCol}</TableHead>
              <TableHead className="text-right font-semibold text-xs whitespace-nowrap" style={{ color: CLR_CM_COL, ...BR_COM }}>{t.wallet.total}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasBets ? (
              <TableRow>
                <TableCell colSpan={totalCols} className="text-center py-10 text-muted-foreground">
                  {t.wallet.noBets}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {treeRootIds.map(rootId => {
                  const rootUser = allUsersForTree.find(u => u.id === rootId);
                  if (!rootUser) return null;
                  // Live fight% used only as fallback; snapshot-based calculations are done inside computeNode
                  const effectiveRootFightPct = rootUser.parentFightPct ?? 0;
                  // Get fight setting timestamp for this viewer→root relationship
                  const rootFightSetting = getFightSetting(effectiveViewerId ?? user.id, rootId);
                  const rootFightUpdatedAt = rootFightSetting.updatedAt;
                  const result = computeNode(rootId, allUsersForTree, betsByUser, viewerTP, effectiveRootFightPct);
                  if (!result) return null;

                  // Determine display row:
                  // - Company view: use ownSummary (agent's own grand total, snapshot-correct internally)
                  // - Agent view (incl. "view as"): for agent children → snapshot-based agentAsChildRow sum
                  //   for player children → parentViewRow (already snapshot-correct)
                  let displayRow: BookRow;
                  // Track the snapshot-derived fight% to pass as label (may differ from live when history exists)
                  let derivedFightPctInt = effectiveRootFightPct;
                  const isAgentViewMode = !isCompanyViewer || !!viewAsAgentId;
                  if (isAgentViewMode && rootUser.tier !== UserTier.PLAYER) {
                    // Agent mode: build displayRow using per-bet snapshot fight% bands
                    // so historical bets are not affected by current live fight% changes
                    const rootDescBets = collectPlayerBetsUnder(rootId, allUsersForTree, betsByUser);
                    if (rootDescBets.length > 0) {
                      const rootBands: Array<{ pct: number; bets: BetRow[] }> = [];
                      for (const bet of rootDescBets) {
                        const pct = deriveParentFightPctForBet(bet, effectiveRootFightPct);
                        const last = rootBands[rootBands.length - 1];
                        if (last && Math.abs(last.pct - pct) < 0.005) {
                          last.bets.push(bet);
                        } else {
                          rootBands.push({ pct, bets: [bet] });
                        }
                      }
                      let combined = emptyBookRow();
                      for (const band of rootBands) {
                        const bandPct = Math.round(band.pct);
                        const bandBetIds = new Set(band.bets.map(b => b.id));
                        const restrictedMap = new Map<string, BetRow[]>();
                        for (const [uid, ubets] of betsByUser.entries()) {
                          const filtered = ubets.filter(b => bandBetIds.has(b.id));
                          if (filtered.length > 0) restrictedMap.set(uid, filtered);
                        }
                        const bandResult = computeNode(rootId, allUsersForTree, restrictedMap, viewerTP, bandPct);
                        if (!bandResult) continue;
                        const bandAsChild = agentAsChildRow(
                          bandResult.ownSummary,
                          rootUser.ticketPrice / 100,
                          viewerTP / 100,
                          bandPct / 100
                        );
                        combined = addRows(combined, bandAsChild);
                      }
                      displayRow = combined;
                      // If all bets fell into ONE band, use that band's derived fight% as label
                      if (rootBands.length === 1) derivedFightPctInt = Math.round(rootBands[0].pct);
                    } else {
                      displayRow = agentAsChildRow(
                        result.ownSummary,
                        rootUser.ticketPrice / 100,
                        viewerTP / 100,
                        effectiveRootFightPct / 100
                      );
                    }
                  } else if (!isAgentViewMode && rootUser.tier !== UserTier.PLAYER) {
                    // Company mode: use companyAgentRow formula per spec
                    displayRow = companyAgentRow(result.ownSummary);
                  } else {
                    // Player child
                    displayRow = result.parentViewRow;
                  }

                  return (
                    <TreeNode
                      key={rootId}
                      nodeId={rootId}
                      displayRow={displayRow}
                      ownSummary={result.ownSummary}
                      fightPctInt={derivedFightPctInt}
                      fightUpdatedAt={rootFightUpdatedAt}
                      snapshotGroups={result.snapshotGroups}
                      allUsers={allUsersForTree}
                      betsByUser={betsByUser}
                      showDetails={showDetails}
                      allMatches={matches}
                      depth={0}
                      parentTP={viewerTP}
                      isCompanyView={isCompanyViewer && !viewAsAgentId}
                      onViewAs={rootUser.tier !== UserTier.PLAYER ? (agentId) => setViewAsAgentId(agentId) : undefined}
                      onViewAsPlayer={rootUser.tier === UserTier.PLAYER ? (playerId) => setViewAsPlayerId(playerId) : undefined}
                    />
                  );
                })}
                {/* Grand Total row — shown for ALL viewers (Company + Agent) */}
                {hasBets && (() => {
                  let grandOrigBP = 0;
                  let grandBpT    = 0, grandCommT  = 0, grandWlT    = 0, grandTotalT = 0;
                  let grandBpF    = 0, grandCommF  = 0, grandWlF    = 0, grandTotalF = 0;
                  let grandBpC    = 0, grandCommC  = 0, grandWlC    = 0, grandTotalC = 0;

                  const isAgentViewModeGT = !isCompanyViewer || !!viewAsAgentId;

                  treeRootIds.forEach(rootId => {
                    const rootUser = allUsersForTree.find(u => u.id === rootId);
                    if (!rootUser) return;
                    const effectiveRootFightPctG = rootUser.parentFightPct ?? 0;
                    const result = computeNode(rootId, allUsersForTree, betsByUser, viewerTP, effectiveRootFightPctG);
                    if (!result) return;

                    // Use same snapshot-based logic as display rows
                    let row: BookRow;
                    if (isAgentViewModeGT && rootUser.tier !== UserTier.PLAYER) {
                      // Snapshot-based agent row sum
                      const rootDescBetsG = collectPlayerBetsUnder(rootId, allUsersForTree, betsByUser);
                      if (rootDescBetsG.length > 0) {
                        const rootBandsG: Array<{ pct: number; bets: BetRow[] }> = [];
                        for (const bet of rootDescBetsG) {
                          const pct = deriveParentFightPctForBet(bet, effectiveRootFightPctG);
                          const last = rootBandsG[rootBandsG.length - 1];
                          if (last && Math.abs(last.pct - pct) < 0.005) {
                            last.bets.push(bet);
                          } else {
                            rootBandsG.push({ pct, bets: [bet] });
                          }
                        }
                        let combinedG = emptyBookRow();
                        for (const band of rootBandsG) {
                          const bandPct = Math.round(band.pct);
                          const bandBetIds = new Set(band.bets.map(b => b.id));
                          const restrictedMap = new Map<string, BetRow[]>();
                          for (const [uid, ubets] of betsByUser.entries()) {
                            const filtered = ubets.filter(b => bandBetIds.has(b.id));
                            if (filtered.length > 0) restrictedMap.set(uid, filtered);
                          }
                          const bandResult = computeNode(rootId, allUsersForTree, restrictedMap, viewerTP, bandPct);
                          if (!bandResult) continue;
                          combinedG = addRows(combinedG, agentAsChildRow(
                            bandResult.ownSummary,
                            rootUser.ticketPrice / 100,
                            viewerTP / 100,
                            bandPct / 100
                          ));
                        }
                        row = combinedG;
                      } else {
                        row = agentAsChildRow(
                          result.ownSummary,
                          rootUser.ticketPrice / 100,
                          viewerTP / 100,
                          effectiveRootFightPctG / 100
                        );
                      }
                    } else if (!isAgentViewModeGT && rootUser.tier !== UserTier.PLAYER) {
                      // Company mode: use companyAgentRow formula per spec
                      row = companyAgentRow(result.ownSummary);
                    } else {
                      row = result.parentViewRow;
                    }

                    grandOrigBP += row.origBP;
                    grandBpT    += row.bpT;   grandCommT  += row.commT;  grandWlT    += row.wlT;   grandTotalT += row.totalT;
                    grandBpF    += row.bpF;   grandCommF  += row.commF;  grandWlF    += row.wlF;   grandTotalF += row.totalF;
                    grandBpC    += row.bpC;   grandCommC  += row.commC;
                    grandWlC    += row.wlC;   grandTotalC += row.totalC;
                  });

                  return (
                    <TableRow className="font-bold border-t-2" style={{ backgroundColor: '#D9E1F2' }}>
                      <TableCell className="text-xs font-bold" style={{ paddingLeft: '8px', color: '#1F3864' }}>Grand Total</TableCell>
                      {showDetails && (<><TableCell /><TableCell /><TableCell style={BL_TOT} /></>)}
                      <C v={grandOrigBP} style={{ ...BL_TOT, fontWeight: 'bold' }} bold noColor />
                      <C v={grandBpT}    bold noColor />
                      <C v={grandCommT}  bold noColor />
                      <C v={grandWlT}    bold noColor />
                      <C v={grandTotalT} bold style={BR_TOT} />
                      <C v={grandBpF}    style={BL_FGT} bold noColor />
                      <C v={grandCommF}  bold />
                      <C v={grandWlF}    bold />
                      <C v={grandTotalF} bold style={BR_FGT} />
                      <C v={grandBpC}    style={BL_COM} bold noColor />
                      <C v={grandCommC}  bold />
                      <C v={grandWlC}    bold />
                      <C v={grandTotalC} bold style={BR_COM} />
                    </TableRow>
                  );
                })()}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}